import { inject, injectable } from "inversify";
import { DependencyType } from "@di/DependencyType";
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import {
  ProductTypesDocument,
  ProductTypesQuery,
  ProductTypesQueryVariables,
} from "generated/graphql";

type ProductTypeNode = NonNullable<NonNullable<ProductTypesQuery["productTypes"]>["nodes"]>[number];

@injectable()
export class ProductTypeService {
  constructor(
    @inject(DependencyType.ApolloClient)
    private readonly apolloClient: ApolloClient<NormalizedCacheObject>
  ) {}

  async getAllProductTypes(refresh = false) {
    const fetchPolicy = refresh ? "network-only" : "cache-first" as const;
    const all: ProductTypeNode[] = [];
    let after: string | undefined;

    while (true) {
      const result = await this.apolloClient.query<
        ProductTypesQuery,
        ProductTypesQueryVariables
      >({
        query: ProductTypesDocument,
        variables: { after },
        fetchPolicy,
      });

      if (result.errors || !result.data?.productTypes) {
        throw new Error("Failed to get product types");
      }

      const connection = result.data.productTypes;
      all.push(...(connection.nodes ?? []));
      if (!connection.pageInfo?.hasNextPage || !connection.pageInfo.endCursor) {
        break;
      }
      after = connection.pageInfo.endCursor;
    }

    return all;
  }
}
